import { Box, createStyles, Stack } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { ReactNode } from 'react';
import React, { useMemo, useState } from 'react';
import { ImpersonationBanner } from '../ImpersonationBanner/ImpersonationBanner';
import SecodaNavBar from '../SecodaAppShell/SecodaNavBar/SecodaNavBar';
import { sideBarStore } from '../SecodaAppShell/SideBar/store';
import { NavBarContext } from './NavBarContext';

interface IBodyWrapperProps {
	children: React.ReactNode;
	hideNavbar?: boolean;
}

const useStyles = createStyles(
	(
		theme,
		{
			showSideBar,
			sideBarWidth,
		}: {
			showSideBar: boolean;
			sideBarWidth: number;
		}
	) => ({
		container: {
			paddingLeft: showSideBar ? sideBarWidth : 0,
			transition: 'margin-left 0.1ms ease-in-out',
			height: '100vh',
			overflow: 'hidden',
			display: 'flex',
			flexGrow: 1,
			flexDirection: 'column',

			[theme.fn.smallerThan('md')]: {
				paddingLeft: 0,
			},

			'@media print': {
				paddingLeft: 0,
			},
		},
		bodyWrapper: {
			display: 'flex',
			flexDirection: 'column',
			flexGrow: 1,
			overflow: 'auto',
		},
	})
);

function BodyWrapper({ children, hideNavbar = false }: IBodyWrapperProps) {
	const { classes, cx } = useStyles({
		showSideBar: sideBarStore.showSideBar && !sideBarStore.collapsed,
		sideBarWidth: sideBarStore.width,
	});

	const [leftNode, setLeftNode] = useState<ReactNode | null>(null);
	const [rightNode, setRightNode] = useState<ReactNode | null>(null);

	const providerValue = useMemo(
		() => ({ setLeftNode, setRightNode, leftNode, rightNode }),
		[leftNode, rightNode]
	);

	return (
		<NavBarContext.Provider value={providerValue}>
			<Stack className={cx(classes.container, 'BodyWrapper')} spacing={0}>
				<ImpersonationBanner />
				{!hideNavbar && <SecodaNavBar />}
				<Box className={classes.bodyWrapper}>{children}</Box>
			</Stack>
		</NavBarContext.Provider>
	);
}

const BodyWrapperObserver = observer(BodyWrapper);
export default BodyWrapperObserver;
